package com.example.readingnotes.presentation.notificationhistory

import com.example.readingnotes.domain.usecase.GetNotificationsUseCase
import com.example.readingnotes.domain.usecase.MarkAllNotificationsAsReadUseCase
import com.example.readingnotes.domain.usecase.MarkNotificationAsReadUseCase
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class NotificationHistoryViewModel(
    private val getNotificationsUseCase: GetNotificationsUseCase,
    private val markNotificationAsReadUseCase: MarkNotificationAsReadUseCase,
    private val markAllNotificationsAsReadUseCase: MarkAllNotificationsAsReadUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(NotificationHistoryState())
    val state: StateFlow<NotificationHistoryState> = _state.asStateFlow()

    fun onEvent(event: NotificationHistoryEvent) {
        when (event) {
            is NotificationHistoryEvent.OnLoadNotifications -> loadNotifications()
            is NotificationHistoryEvent.OnLoadMore -> loadMoreNotifications()
            is NotificationHistoryEvent.OnMarkAsRead -> markAsRead(event.id)
            is NotificationHistoryEvent.OnMarkAllAsRead -> markAllAsRead()
            is NotificationHistoryEvent.OnToggleUnreadFilter -> toggleUnreadFilter()
            is NotificationHistoryEvent.OnClearSuccess -> clearSuccess()
        }
    }

    private fun loadNotifications() {
        viewModelScope.launch {
            _state.update {
                it.copy(
                    isLoading = true,
                    error = null,
                    unreadOnly = _state.value.unreadOnly,
                )
            }

            getNotificationsUseCase(
                limit = DEFAULT_LIMIT,
                offset = 0,
                unreadOnly = _state.value.unreadOnly,
            ).onSuccess { paginatedNotifications ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null,
                        notifications = paginatedNotifications.notifications,
                        total = paginatedNotifications.total,
                        hasMore = paginatedNotifications.hasMore,
                        currentOffset = DEFAULT_LIMIT,
                    )
                }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = ErrorMessageProvider.getLocalizedMessage(error, "通知の読み込みに失敗しました"),
                    )
                }
            }
        }
    }

    private fun loadMoreNotifications() {
        val currentState = _state.value
        if (currentState.isLoadingMore || !currentState.hasMore) return

        viewModelScope.launch {
            _state.update { it.copy(isLoadingMore = true) }

            getNotificationsUseCase(
                limit = DEFAULT_LIMIT,
                offset = currentState.currentOffset,
                unreadOnly = currentState.unreadOnly,
            ).onSuccess { paginatedNotifications ->
                _state.update {
                    it.copy(
                        isLoadingMore = false,
                        error = null,
                        notifications = currentState.notifications + paginatedNotifications.notifications,
                        hasMore = paginatedNotifications.hasMore,
                        currentOffset = currentState.currentOffset + DEFAULT_LIMIT,
                    )
                }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isLoadingMore = false,
                        error = ErrorMessageProvider.getLocalizedMessage(error, "追加読み込みに失敗しました"),
                    )
                }
            }
        }
    }

    private fun markAsRead(id: String) {
        viewModelScope.launch {
            markNotificationAsReadUseCase(id)
                .onSuccess {
                    _state.update { currentState ->
                        currentState.copy(
                            notifications =
                                currentState.notifications.map { notification ->
                                    if (notification.id == id) {
                                        notification.copy(isRead = true)
                                    } else {
                                        notification
                                    }
                                },
                        )
                    }
                }.onFailure { error ->
                    // Log error but don't show UI for individual mark failure
                    // Error logged silently - individual mark failures are not critical
                }
        }
    }

    private fun markAllAsRead() {
        viewModelScope.launch {
            _state.update { it.copy(isMarkingAllRead = true) }

            markAllNotificationsAsReadUseCase()
                .onSuccess { count ->
                    _state.update { currentState ->
                        currentState.copy(
                            isMarkingAllRead = false,
                            notifications = currentState.notifications.map { it.copy(isRead = true) },
                            allReadSuccess = count,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isMarkingAllRead = false,
                            error = ErrorMessageProvider.getLocalizedMessage(error, "一括既読化に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun toggleUnreadFilter() {
        _state.update { it.copy(unreadOnly = !it.unreadOnly) }
        loadNotifications()
    }

    private fun clearSuccess() {
        _state.update { it.copy(allReadSuccess = null) }
    }

    companion object {
        private const val DEFAULT_LIMIT = 30
    }
}

data class NotificationHistoryState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val isMarkingAllRead: Boolean = false,
    val error: String? = null,
    val notifications: List<com.example.readingnotes.domain.model.Notification> = emptyList(),
    val total: Int = 0,
    val hasMore: Boolean = false,
    val currentOffset: Int = 0,
    val unreadOnly: Boolean = false,
    val allReadSuccess: Int? = null, // Number of notifications marked as read
)

sealed interface NotificationHistoryEvent {
    data object OnLoadNotifications : NotificationHistoryEvent

    data object OnLoadMore : NotificationHistoryEvent

    data class OnMarkAsRead(
        val id: String,
    ) : NotificationHistoryEvent

    data object OnMarkAllAsRead : NotificationHistoryEvent

    data object OnToggleUnreadFilter : NotificationHistoryEvent

    data object OnClearSuccess : NotificationHistoryEvent
}
